import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate, inr } from "@/lib/api";
import { PageHeader, StageBadge } from "@/components/app/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Phone, MessageSquare, Calendar, Edit3 } from "lucide-react";
import { toast } from "sonner";
import Documents from "@/components/app/Documents";
import SearchablePicker from "@/components/app/SearchablePicker";
import EmployeePicker from "@/components/app/EmployeePicker";

const STAGES = ["New Lead", "Contacted", "Site Survey Scheduled", "Proposal Sent", "Negotiation", "Won", "Lost"];
const SOURCES = ["Website", "Facebook", "Google Ads", "WhatsApp", "Referral", "Employee Referral"];

export default function LeadDetail() {
  const { id } = useParams();
  const [lead, setLead] = useState(null);
  const [callNote, setCallNote] = useState("");
  const [followUp, setFollowUp] = useState({ due_at: "", note: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [visitDialogOpen, setVisitDialogOpen] = useState(false);

  const load = () => api.get(`/leads/${id}`).then((r) => setLead(r.data));
  useEffect(() => { load(); }, [id]);

  if (!lead) return <div className="p-4 sm:p-8 label-eyebrow">Loading…</div>;

  const addCall = async () => {
    if (!callNote.trim()) return;
    await api.post(`/leads/${id}/calls`, { note: callNote });
    setCallNote(""); toast.success("Call logged"); load();
  };
  const addFollowUp = async () => {
    if (!followUp.due_at || !followUp.note) return;
    await api.post(`/leads/${id}/follow-ups`, followUp);
    setFollowUp({ due_at: "", note: "" }); toast.success("Follow-up added"); load();
  };

  return (
    <div data-testid="lead-detail-page">
      <PageHeader
        eyebrow={`Lead #${lead.id.slice(0, 8)}`}
        title={lead.name}
        subtitle={`${lead.source} · ${lead.address || "Location not set"}`}
        action={
          <div className="flex items-center gap-3">
            <StageBadge stage={lead.stage} />
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold h-10" data-testid="edit-lead-button">
                  <Edit3 className="w-3 h-3 mr-2" /> Edit Lead
                </Button>
              </DialogTrigger>
              <EditLeadDialog lead={lead} onDone={() => { setEditOpen(false); load(); }} />
            </Dialog>
          </div>
        }
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-8">
        <div className="lg:col-span-2 space-y-6">
          <Section title="Contact">
            <Row label="Phone" value={lead.phone} icon={Phone} />
            <Row label="Email" value={lead.email || "—"} icon={MessageSquare} />
            <Row label="Address" value={lead.address || "—"} />
            <Row
              label="Map Link"
              value={
                lead.map_link
                  ? <a href={lead.map_link} target="_blank" rel="noreferrer" className="text-[#002FA7] underline break-all" data-testid="lead-map-link">Open in Google Maps ↗</a>
                  : "—"
              }
            />
            <Row label="Consumer Number" value={lead.consumer_number || "—"} />
            <Row label="Transformer" value={lead.transformer_kva ? `${lead.transformer_kva} kVA` : "—"} />
            <Row label="Monthly Consumption" value={lead.monthly_consumption_kwh ? `${lead.monthly_consumption_kwh} kWh` : "—"} />
            <Row label="Monthly Bill" value={lead.monthly_bill_inr ? inr(lead.monthly_bill_inr) : "—"} />
            <Row label="Inverter Capacity" value={lead.capacity_kw ? `${lead.capacity_kw} kW` : "—"} />
            <Row label="Panel Capacity" value={lead.panel_capacity_kw ? `${lead.panel_capacity_kw} kW` : "—"} />
            <Row label="Status" value={lead.status || lead.stage || "—"} />
            <Row label="Temperature" value={lead.temperature || "—"} />
            <Row label="Assignee" value={lead.assignee_name || "—"} />
            <Row label="Next Follow-Up" value={lead.next_followup_date ? `${lead.next_followup_date}${lead.next_followup_time ? ` · ${lead.next_followup_time}` : ""}${lead.followup_priority ? ` · ${lead.followup_priority}` : ""}` : "—"} />
            <Row label="Last Follow-Up" value={lead.last_followup_date || "—"} />
            <Row label="Created" value={fmtDate(lead.created_at)} icon={Calendar} />
          </Section>

          <Section title="Call Logs">
            <div className="flex gap-2 mb-4">
              <Input value={callNote} onChange={(e) => setCallNote(e.target.value)} placeholder="Quick call note…" className="rounded-none" data-testid="call-note-input" />
              <Button onClick={addCall} className="rounded-none bg-[#0a0a0a] hover:bg-[#0a0a0a]/80 uppercase tracking-widest text-xs font-bold" data-testid="add-call-button">Log Call</Button>
            </div>
            <div className="space-y-2">
              {(lead.call_logs || []).slice().reverse().map((c) => (
                <div key={c.id} className="border border-[#E5E7EB] p-3 text-sm">
                  <div className="text-xs text-[#4B5563] mb-1">{fmtDate(c.at)} · {c.by}</div>
                  <div>{c.note}</div>
                </div>
              ))}
              {(lead.call_logs || []).length === 0 && <div className="text-xs text-[#9CA3AF]">No calls yet.</div>}
            </div>
          </Section>

          <Section title="Follow-ups">
            <div className="grid grid-cols-3 gap-2 mb-4">
              <Input type="date" value={followUp.due_at} onChange={(e) => setFollowUp({ ...followUp, due_at: e.target.value })} className="rounded-none" data-testid="follow-up-date" />
              <Input value={followUp.note} onChange={(e) => setFollowUp({ ...followUp, note: e.target.value })} placeholder="What's the next step?" className="rounded-none col-span-2" data-testid="follow-up-note" />
              <Button onClick={addFollowUp} className="col-span-3 rounded-none bg-[#0a0a0a] hover:bg-[#0a0a0a]/80 uppercase tracking-widest text-xs font-bold" data-testid="add-follow-up-button">Schedule Follow-up</Button>
            </div>
            <div className="space-y-2">
              {(lead.follow_ups || []).slice().reverse().map((f) => (
                <div key={f.id} className="border border-[#E5E7EB] p-3 text-sm">
                  <div className="text-xs text-[#4B5563] mb-1">Due {fmtDate(f.due_at)}</div>
                  <div>{f.note}</div>
                </div>
              ))}
              {(lead.follow_ups || []).length === 0 && <div className="text-xs text-[#9CA3AF]">No follow-ups scheduled.</div>}
            </div>
          </Section>
        </div>

        <div className="space-y-6">
          <Section title="Quick Actions">
            <div className="space-y-2">
              <Dialog open={visitDialogOpen} onOpenChange={setVisitDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="w-full rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold" data-testid="schedule-survey-button">+ Schedule Site Visit</Button>
                </DialogTrigger>
                <ScheduleSalesVisitDialog lead={lead} onDone={() => { setVisitDialogOpen(false); load(); }} />
              </Dialog>
              <Link to="/proposals" state={{ leadId: id }}>
                <Button variant="outline" className="w-full rounded-none border-[#0a0a0a] uppercase tracking-widest text-xs font-bold mt-2" data-testid="create-proposal-link">+ Proposal</Button>
              </Link>
            </div>
          </Section>
          <Section title="Notes">
            <p className="text-sm text-[#4B5563] whitespace-pre-wrap">{lead.notes || "—"}</p>
          </Section>
          <div className="flat-card p-6">
            <Documents ownerType="lead" ownerId={id} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="flat-card p-6">
      <div className="label-eyebrow mb-4">{title}</div>
      {children}
    </div>
  );
}
function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex justify-between py-2 border-b border-[#F3F4F6] last:border-0">
      <div className="flex items-center gap-2 text-xs text-[#4B5563]">
        {Icon && <Icon className="w-3 h-3" />}
        <span className="uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}

function EditLeadDialog({ lead, onDone }) {
  const [f, setF] = useState({
    name: lead.name || "",
    phone: lead.phone || "",
    email: lead.email || "",
    source: lead.source || "Website",
    referral_name: lead.referral_name || "",
    referral_user_id: lead.referral_user_id || "",
    capacity_kw: lead.capacity_kw ?? "",
    panel_capacity_kw: lead.panel_capacity_kw ?? "",
    address: lead.address || "",
    map_link: lead.map_link || "",
    consumer_number: lead.consumer_number || "",
    transformer_kva: lead.transformer_kva ?? "",
    monthly_consumption_kwh: lead.monthly_consumption_kwh ?? "",
    monthly_bill_inr: lead.monthly_bill_inr ?? "",
    stage: lead.stage || "New Lead",
    status: lead.status || lead.stage || "New Lead",
    temperature: lead.temperature || "",
    assignee_id: lead.assignee_id || "",
    assignee_name: lead.assignee_name || "",
    next_followup_date: lead.next_followup_date || "",
    next_followup_time: lead.next_followup_time || "",
    followup_priority: lead.followup_priority || "Medium",
    notes: lead.notes || "",
  });
  const [employees, setEmployees] = useState([]);
  useEffect(() => { api.get("/users").then((r) => setEmployees((r.data || []).filter((u) => u.role !== "customer"))); }, []);
  const set = (k) => (v) => setF({ ...f, [k]: v.target ? v.target.value : v });

  const submit = async () => {
    if (!f.name || !f.phone) { toast.error("Name and phone required"); return; }
    try {
      if (f.source === "Referral" && !f.referral_name?.trim()) {
        toast.error("Referral name is required when Source is Referral");
        return;
      }
      if (f.source === "Employee Referral" && !f.referral_user_id) {
        toast.error("Select the referring employee");
        return;
      }
      await api.patch(`/leads/${lead.id}`, {
        ...f,
        referral_name: ["Referral", "Employee Referral"].includes(f.source) ? ((f.referral_name || "").trim() || null) : null,
        referral_user_id: f.source === "Employee Referral" ? f.referral_user_id : null,
        capacity_kw: f.capacity_kw === "" ? null : parseFloat(f.capacity_kw),
        panel_capacity_kw: f.panel_capacity_kw === "" ? null : parseFloat(f.panel_capacity_kw),
        transformer_kva: f.transformer_kva === "" ? null : parseFloat(f.transformer_kva),
        monthly_consumption_kwh: f.monthly_consumption_kwh === "" ? null : parseFloat(f.monthly_consumption_kwh),
        monthly_bill_inr: f.monthly_bill_inr === "" ? null : parseFloat(f.monthly_bill_inr),
        temperature: f.temperature || null,
        assignee_id: f.assignee_id || null,
        assignee_name: f.assignee_name || null,
        next_followup_date: f.next_followup_date || null,
        next_followup_time: f.next_followup_time || null,
        followup_priority: f.followup_priority || null,
      });
      toast.success("Lead updated");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader><DialogTitle className="font-black text-2xl tracking-tight">Edit Lead</DialogTitle></DialogHeader>
      <div className="space-y-3 pt-2">
        <div className="grid grid-cols-2 gap-3">
          <EField label="Full Name" value={f.name} onChange={set("name")} testid="edit-lead-name" />
          <EField label="Phone" value={f.phone} onChange={set("phone")} testid="edit-lead-phone" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <EField label="Email" value={f.email} onChange={set("email")} testid="edit-lead-email" />
          <div>
            <Label className="label-eyebrow">Source</Label>
            <Select value={f.source} onValueChange={set("source")}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="edit-lead-source"><SelectValue /></SelectTrigger>
              <SelectContent>{SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        {f.source === "Referral" && (
          <div>
            <Label className="label-eyebrow">Referred By *</Label>
            <Input
              value={f.referral_name}
              onChange={set("referral_name")}
              placeholder="Name of the person who referred this lead"
              className="rounded-none mt-2 h-10"
              data-testid="edit-lead-referral-name"
            />
          </div>
        )}
        {f.source === "Employee Referral" && (
          <div>
            <Label className="label-eyebrow">Referred By (Employee) *</Label>
            <div className="mt-2">
              <EmployeePicker
                employees={employees}
                value={f.referral_user_id}
                onChange={(id, u) => setF({ ...f, referral_user_id: id || "", referral_name: u?.name || "" })}
                placeholder="Search employee…"
                testid="edit-lead-referral-employee"
                allowUnassigned={false}
              />
            </div>
          </div>
        )}
        <EField label="Address" value={f.address} onChange={set("address")} testid="edit-lead-address" />
        <div>
          <Label className="label-eyebrow">Google Maps Link</Label>
          <Input
            value={f.map_link}
            onChange={set("map_link")}
            placeholder="Paste link from WhatsApp (https://maps.app.goo.gl/…)"
            className="rounded-none mt-2 h-10"
            data-testid="edit-lead-map-link"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <EField label="Consumer Number" value={f.consumer_number} onChange={set("consumer_number")} testid="edit-lead-consumer-number" />
          <EField label="Transformer (kVA)" type="number" value={f.transformer_kva} onChange={set("transformer_kva")} testid="edit-lead-transformer-kva" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <EField label="Monthly Consumption (kWh)" type="number" value={f.monthly_consumption_kwh} onChange={set("monthly_consumption_kwh")} testid="edit-lead-monthly-consumption" />
          <EField label="Monthly Bill (₹)" type="number" value={f.monthly_bill_inr} onChange={set("monthly_bill_inr")} testid="edit-lead-monthly-bill" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <EField label="Inverter Capacity (kW)" type="number" value={f.capacity_kw} onChange={set("capacity_kw")} testid="edit-lead-capacity" />
          <EField label="Panel Capacity (kW)" type="number" value={f.panel_capacity_kw} onChange={set("panel_capacity_kw")} testid="edit-lead-panel-capacity" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div />
          <div>
            <Label className="label-eyebrow">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v, stage: ["New Lead","Contacted","Site Survey Scheduled","Proposal Sent","Negotiation","Won","Lost"].includes(v) ? v : f.stage })}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="edit-lead-status"><SelectValue /></SelectTrigger>
              <SelectContent>{["New Lead","Contacted","Site Survey Scheduled","Site Survey Completed","Proposal Sent","Negotiation","Won","Lost","Dormant"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Temperature</Label>
            <Select value={f.temperature || "_none"} onValueChange={(v) => set("temperature")(v === "_none" ? "" : v)}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="edit-lead-temperature"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent><SelectItem value="_none">— None —</SelectItem>{["Super Hot","Hot","Warm","Cold"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-eyebrow">Assignee</Label>
            <Select
              value={f.assignee_id || "_none"}
              onValueChange={(v) => {
                if (v === "_none") { setF({ ...f, assignee_id: "", assignee_name: "" }); return; }
                const u = employees.find((e) => e.id === v);
                setF({ ...f, assignee_id: v, assignee_name: u?.name || "" });
              }}
            >
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="edit-lead-assignee"><SelectValue placeholder="Pick user" /></SelectTrigger>
              <SelectContent><SelectItem value="_none">— Unassigned —</SelectItem>{employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <EField label="Next FU Date" type="date" value={f.next_followup_date} onChange={set("next_followup_date")} testid="edit-lead-next-fu-date" />
          <EField label="Time" type="time" value={f.next_followup_time} onChange={set("next_followup_time")} testid="edit-lead-next-fu-time" />
          <div>
            <Label className="label-eyebrow">Priority</Label>
            <Select value={f.followup_priority} onValueChange={set("followup_priority")}>
              <SelectTrigger className="rounded-none mt-2 h-10" data-testid="edit-lead-fu-priority"><SelectValue /></SelectTrigger>
              <SelectContent>{["High","Medium","Low"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="label-eyebrow">Notes</Label>
          <Textarea value={f.notes} onChange={set("notes")} className="rounded-none mt-2" rows={3} data-testid="edit-lead-notes" />
        </div>
        <Button onClick={submit} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="edit-lead-submit">
          Save Changes
        </Button>
      </div>
    </DialogContent>
  );
}

function EField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input type={type} value={value} onChange={onChange} className="rounded-none mt-2 h-10" data-testid={testid} />
    </div>
  );
}

function ScheduleSalesVisitDialog({ lead, onDone }) {
  // Default to TOMORROW so site teams have a buffer day to plan the run.
  const tomorrow = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);
  const [f, setF] = useState({
    scheduled_date: tomorrow,
    assignee_id: lead?.assignee_id || "",
    assignee_name: lead?.assignee_name || "",
    map_link: lead?.map_link || "",
    roof_type: "",
    monthly_consumption_kwh: lead?.monthly_consumption_kwh ?? "",
    monthly_bill_inr: lead?.monthly_bill_inr ?? "",
    transformer_kva: lead?.transformer_kva ?? "",
    capacity_kw: lead?.capacity_kw ?? "",
    panel_capacity_kw: lead?.panel_capacity_kw ?? "",
    shadow_notes: "",
    notes: "",
  });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!f.scheduled_date) { toast.error("Pick a visit date"); return; }
    setSubmitting(true);
    try {
      await api.post("/sales-visits", {
        lead_id: lead.id,
        scheduled_date: f.scheduled_date,
        assignee_id: f.assignee_id || null,
        assignee_name: f.assignee_name || null,
        map_link: f.map_link || null,
        roof_type: f.roof_type || null,
        monthly_consumption_kwh: num(f.monthly_consumption_kwh),
        monthly_bill_inr: num(f.monthly_bill_inr),
        transformer_kva: num(f.transformer_kva),
        capacity_kw: num(f.capacity_kw),
        panel_capacity_kw: num(f.panel_capacity_kw),
        shadow_notes: f.shadow_notes || null,
        notes: f.notes || null,
      });
      toast.success("Sales site visit scheduled");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to schedule");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent className="rounded-none border-[#0a0a0a] max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-black text-2xl tracking-tight">Schedule Site Visit</DialogTitle>
        <p className="text-[11px] text-[#4B5563] mt-1">
          Creates an entry in <b>Sales Closing Site Visits</b>. Data is pre-filled from the lead — adjust before saving.
        </p>
      </DialogHeader>
      <div className="space-y-3 pt-2">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Visit Date</Label>
            <Input
              type="date"
              value={f.scheduled_date}
              onChange={set("scheduled_date")}
              className="rounded-none mt-2 h-10"
              data-testid="visit-date-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Assignee</Label>
            <SearchablePicker
              items={users}
              value={f.assignee_id}
              onChange={(uid) => {
                const u = users.find((x) => x.id === uid);
                setF({ ...f, assignee_id: uid, assignee_name: u?.name || "" });
              }}
              matchItem={(u, needle) =>
                (u.name || "").toLowerCase().includes(needle) ||
                (u.email || "").toLowerCase().includes(needle) ||
                (u.role || "").toLowerCase().includes(needle)
              }
              renderRow={(u) => (
                <div>
                  <div className="font-bold">{u.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#9CA3AF]">
                    {u.role} · {u.email}
                  </div>
                </div>
              )}
              selectedLabel={(u) => u.name}
              placeholder="Pick a teammate…"
              emptyHint="No teammates match."
              testid="visit-assignee"
            />
          </div>
        </div>
        <div>
          <Label className="label-eyebrow">Google Map Link</Label>
          <Input
            value={f.map_link}
            onChange={set("map_link")}
            placeholder="https://maps.google.com/?q=…"
            className="rounded-none mt-2 h-10"
            data-testid="visit-map-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Roof Type</Label>
            <Input
              value={f.roof_type}
              onChange={set("roof_type")}
              placeholder="RCC / Tin / Tile / Ground"
              className="rounded-none mt-2 h-10"
              data-testid="visit-roof-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Transformer (kVA)</Label>
            <Input
              type="number"
              value={f.transformer_kva}
              onChange={set("transformer_kva")}
              className="rounded-none mt-2 h-10"
              data-testid="visit-transformer-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Monthly Consumption (kWh)</Label>
            <Input
              type="number"
              value={f.monthly_consumption_kwh}
              onChange={set("monthly_consumption_kwh")}
              className="rounded-none mt-2 h-10"
              data-testid="visit-consumption-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Monthly Bill (₹)</Label>
            <Input
              type="number"
              value={f.monthly_bill_inr}
              onChange={set("monthly_bill_inr")}
              className="rounded-none mt-2 h-10"
              data-testid="visit-bill-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="label-eyebrow">Inverter Capacity (kW)</Label>
            <Input
              type="number"
              value={f.capacity_kw}
              onChange={set("capacity_kw")}
              className="rounded-none mt-2 h-10"
              data-testid="visit-inv-cap-input"
            />
          </div>
          <div>
            <Label className="label-eyebrow">Panel Capacity (kW)</Label>
            <Input
              type="number"
              value={f.panel_capacity_kw}
              onChange={set("panel_capacity_kw")}
              className="rounded-none mt-2 h-10"
              data-testid="visit-panel-cap-input"
            />
          </div>
        </div>
        <div>
          <Label className="label-eyebrow">Shadow Analysis</Label>
          <Textarea
            value={f.shadow_notes}
            onChange={set("shadow_notes")}
            placeholder="Trees, water tank, neighbouring buildings…"
            className="rounded-none mt-2"
            rows={2}
            data-testid="visit-shadow-input"
          />
        </div>
        <div>
          <Label className="label-eyebrow">Notes</Label>
          <Textarea
            value={f.notes}
            onChange={set("notes")}
            placeholder="Reach by 10am, gate code 4521, contact Mr. Menon on arrival…"
            className="rounded-none mt-2"
            rows={2}
            data-testid="visit-notes-input"
          />
        </div>
        <Button onClick={submit} disabled={submitting} className="w-full rounded-none bg-[#002FA7] hover:bg-[#002480] h-11 uppercase tracking-widest text-xs font-bold" data-testid="visit-submit">
          {submitting ? "Saving…" : "Schedule Visit"}
        </Button>
      </div>
    </DialogContent>
  );
}
